import { useState, useEffect } from 'react';
import { Bot, MessageCircle, Plus, Trash2, Edit, Save, X, Brain, TrendingUp, Clock } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

export default function AIReceptionistPage() {
  const { user } = useAuth();
  const hasAI = user?.subscription && ['basic', 'premium', 'elite'].includes(user.subscription);
  const [settings, setSettings] = useState(null);
  const [knowledge, setKnowledge] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [editingSettings, setEditingSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ name: '', greeting: '', tone: '' });
  
  const [showAddKnowledge, setShowAddKnowledge] = useState(false);
  const [knowledgeForm, setKnowledgeForm] = useState({ question: '', answer: '', category: 'general' });

  useEffect(() => {
    if (hasAI) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [hasAI]);

  const loadData = async () => {
    try {
      const [settingsData, knowledgeData, statsData, convsData] = await Promise.all([
        api.getAISettings(),
        api.getAIKnowledge(),
        api.getAIStats(),
        api.getAIConversations()
      ]);
      setSettings(settingsData);
      setSettingsForm({ 
        name: settingsData.name || '', 
        greeting: settingsData.greeting || '', 
        tone: settingsData.tone || 'profesional' 
      });
      setKnowledge(knowledgeData);
      setStats(statsData);
      setConversations(convsData);
    } catch (e) {
      console.error('Error loading AI data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const updated = await api.updateAISettings(settingsForm);
      setSettings(updated);
      setEditingSettings(false);
      toast.success('Configuración guardada');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleAddKnowledge = async () => {
    if (!knowledgeForm.question.trim() || !knowledgeForm.answer.trim()) {
      toast.error('Completa pregunta y respuesta');
      return;
    }
    try {
      const newEntry = await api.addAIKnowledge(knowledgeForm);
      setKnowledge([newEntry, ...knowledge]);
      setKnowledgeForm({ question: '', answer: '', category: 'general' });
      setShowAddKnowledge(false);
      toast.success('Conocimiento agregado');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDeleteKnowledge = async (id) => {
    try {
      await api.deleteAIKnowledge(id);
      setKnowledge(knowledge.filter(k => k.id !== id));
      toast.success('Eliminado');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!hasAI) {
    const wasDeactivated = user?.ai_features?.deactivated_at;
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center" data-testid="ai-page-locked">
        <div className="w-16 h-16 rounded-2xl bg-purple-500/10 flex items-center justify-center mb-6">
          <Bot className="w-8 h-8 text-purple-400" />
        </div>
        <h2 className="text-2xl font-medium text-zinc-100 mb-2 font-[Outfit]">
          {wasDeactivated ? 'Renueva para reactivar tu Recepcionista IA' : 'Recepcionista IA'}
        </h2>
        <p className="text-zinc-500 max-w-md mb-6">
          {wasDeactivated
            ? 'Tu configuracion, FAQs e historial de conversaciones estan guardados. Al renovar se restauran sin perder contexto.'
            : 'Automatiza tu atencion al cliente 24/7 con tu asistente IA personalizado. Disponible desde el plan Basic por solo $99 MXN/mes.'
          }
        </p>
        <Button onClick={() => window.location.href = '/precios'} className="bg-emerald-600 hover:bg-emerald-500" data-testid="ai-upsell-btn">
          {wasDeactivated ? 'Renovar plan' : 'Ver planes'}
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-32 skeleton rounded-2xl" />
        <div className="h-64 skeleton rounded-2xl" />
      </div>
    );
  }

  return (
    <div data-testid="ai-receptionist-page">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-light tracking-tight text-zinc-100 mb-2 font-[Outfit]">
          Recepcionista IA
        </h1>
        <p className="text-zinc-500">Configura tu asistente virtual para atender clientes 24/7</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="glass-card p-5">
          <MessageCircle className="w-5 h-5 text-purple-400 mb-2" />
          <p className="text-2xl font-semibold text-zinc-100 font-[Outfit]">{stats?.today_conversations || 0}</p>
          <p className="text-sm text-zinc-500">Conversaciones hoy</p>
        </div>
        <div className="glass-card p-5">
          <TrendingUp className="w-5 h-5 text-blue-400 mb-2" />
          <p className="text-2xl font-semibold text-zinc-100 font-[Outfit]">{stats?.total_conversations || 0}</p>
          <p className="text-sm text-zinc-500">Total conversaciones</p>
        </div>
        <div className="glass-card p-5">
          <Brain className="w-5 h-5 text-emerald-400 mb-2" />
          <p className="text-2xl font-semibold text-zinc-100 font-[Outfit]">{knowledge.length}</p>
          <p className="text-sm text-zinc-500">Conocimientos</p>
        </div>
        <div className="glass-card p-5">
          <Clock className="w-5 h-5 text-amber-400 mb-2" />
          <p className="text-2xl font-semibold text-zinc-100 font-[Outfit]">24/7</p>
          <p className="text-sm text-zinc-500">Disponibilidad</p>
        </div>
      </div>

      <Tabs defaultValue="settings" className="space-y-6">
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="settings" className="data-[state=active]:bg-white/10">Configuración</TabsTrigger>
          <TabsTrigger value="knowledge" className="data-[state=active]:bg-white/10">Base de conocimiento</TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-white/10">Historial</TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-zinc-100">Configuración del asistente</h2>
              {editingSettings ? (
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setEditingSettings(false)} className="text-zinc-400">
                    <X className="w-4 h-4 mr-1" />
                    Cancelar
                  </Button>
                  <Button onClick={handleSaveSettings} className="bg-purple-600 hover:bg-purple-500">
                    <Save className="w-4 h-4 mr-1" />
                    Guardar
                  </Button>
                </div>
              ) : (
                <Button variant="outline" onClick={() => setEditingSettings(true)} className="border-white/10 text-zinc-300">
                  <Edit className="w-4 h-4 mr-1" />
                  Editar
                </Button>
              )}
            </div>

            <div className="space-y-6">
              <div>
                <Label className="text-zinc-400 mb-2 block">Nombre del asistente</Label>
                {editingSettings ? (
                  <Input
                    value={settingsForm.name}
                    onChange={(e) => setSettingsForm({ ...settingsForm, name: e.target.value })}
                    className="bg-white/5 border-white/10 text-zinc-100"
                    placeholder="Ej: Ana, el asistente de mi criadero"
                  />
                ) : (
                  <p className="text-zinc-100">{settings?.name || 'No configurado'}</p>
                )}
              </div>

              <div>
                <Label className="text-zinc-400 mb-2 block">Saludo inicial</Label>
                {editingSettings ? (
                  <Textarea
                    value={settingsForm.greeting}
                    onChange={(e) => setSettingsForm({ ...settingsForm, greeting: e.target.value })}
                    className="bg-white/5 border-white/10 text-zinc-100"
                    rows={3}
                    placeholder="Ej: ¡Hola! Soy el asistente virtual del criadero X..."
                  />
                ) : (
                  <p className="text-zinc-100">{settings?.greeting || 'No configurado'}</p>
                )}
              </div>

              <div>
                <Label className="text-zinc-400 mb-2 block">Tono de comunicación</Label>
                {editingSettings ? (
                  <select
                    value={settingsForm.tone}
                    onChange={(e) => setSettingsForm({ ...settingsForm, tone: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-zinc-100 rounded-lg px-3 py-2"
                  >
                    <option value="profesional">Profesional</option>
                    <option value="amigable">Amigable</option>
                    <option value="formal">Formal</option>
                    <option value="casual">Casual</option>
                  </select>
                ) : (
                  <p className="text-zinc-100 capitalize">{settings?.tone || 'profesional'}</p>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Knowledge Tab */}
        <TabsContent value="knowledge">
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-zinc-100">Base de conocimiento</h2>
              <Button onClick={() => setShowAddKnowledge(true)} className="bg-purple-600 hover:bg-purple-500">
                <Plus className="w-4 h-4 mr-1" />
                Agregar
              </Button>
            </div>

            {showAddKnowledge && (
              <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="space-y-4">
                  <div>
                    <Label className="text-zinc-400 mb-2 block">Pregunta frecuente</Label>
                    <Input
                      value={knowledgeForm.question}
                      onChange={(e) => setKnowledgeForm({ ...knowledgeForm, question: e.target.value })}
                      className="bg-white/5 border-white/10 text-zinc-100"
                      placeholder="Ej: ¿Hacen envíos a todo México?"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 mb-2 block">Respuesta</Label>
                    <Textarea
                      value={knowledgeForm.answer}
                      onChange={(e) => setKnowledgeForm({ ...knowledgeForm, answer: e.target.value })}
                      className="bg-white/5 border-white/10 text-zinc-100"
                      rows={3}
                      placeholder="Ej: Sí, hacemos envíos a todo México con paquetería..."
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => setShowAddKnowledge(false)} className="text-zinc-400">
                      Cancelar
                    </Button>
                    <Button onClick={handleAddKnowledge} className="bg-purple-600 hover:bg-purple-500">
                      Guardar
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {knowledge.length > 0 ? (
              <div className="space-y-3">
                {knowledge.map((item) => (
                  <div key={item.id} className="p-4 rounded-xl bg-white/5 border border-white/5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-zinc-100 mb-1">{item.question}</p>
                        <p className="text-sm text-zinc-400">{item.answer}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteKnowledge(item.id)}
                        className="p-2 text-zinc-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-zinc-500 py-8">
                Agrega preguntas y respuestas para entrenar a tu asistente
              </p>
            )}
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <div className="glass-card p-6">
            <h2 className="text-lg font-medium text-zinc-100 mb-6">Historial de conversaciones</h2>
            {conversations.length > 0 ? (
              <div className="space-y-4">
                {conversations.slice(0, 20).map((conv) => (
                  <div key={conv.id} className="p-4 rounded-xl bg-white/5 border border-white/5">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                        <MessageCircle className="w-4 h-4 text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-zinc-400 mb-1">{conv.user_message}</p>
                        <p className="text-sm text-zinc-100">{conv.ai_response}</p>
                        <p className="text-xs text-zinc-600 mt-2">
                          {new Date(conv.created_at).toLocaleString('es-MX')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-zinc-500 py-8">
                Aún no hay conversaciones
              </p>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
